import { useEffect, useMemo, useState } from 'react';
import { Country } from 'countries-and-timezones';
import uniqBy from 'lodash/uniqBy';

import { SortDirection, SortField } from './index';
import { AugmentedTimeZoneWithCountryData } from '../types';
import { countriesCodes } from '../consts';

export const useFilteringWithSortState = (
  augmentedWithExtraData: AugmentedTimeZoneWithCountryData[]
) => {
  const [onlyShowRecordsWithLowHDI, setOnlyShowRecordsWithLowHDI] =
    useState(false);
  const [onlyShowRecordsWithHiringData, setOnlyShowRecordsWithHiringData] =
    useState(false);
  const [searchValues, setSearchValues] = useState<Country[]>([]);

  const [{ sortField, sortDirection }, setSorting] = useState<{
    sortField: SortField;
    sortDirection: SortDirection;
  }>({
    sortField: 'overlap',
    sortDirection: 'asc',
  });

  useEffect(() => {
    setSearchValues([]);
  }, [augmentedWithExtraData]);

  // filters out redundant records
  const filteredData = useMemo<AugmentedTimeZoneWithCountryData[]>(() => {
    return (
      augmentedWithExtraData
        .filter((el) => countriesCodes.includes(el.country.id))
        // .filter((el) =>
        //   onlyShowRecordsWithLowHDI ? el.HDIData?.rating === 'LOW' : el
        // )
        // .filter((el) =>
        //   onlyShowRecordsWithHiringData ? !!el.hiringGuidesData?.url : el
        // )
        .filter(
          (el) =>
            !searchValues.length ||
            searchValues.every(({ id }) => id !== el.country.id)
        )
    );
  }, [
    searchValues,
    augmentedWithExtraData,
    onlyShowRecordsWithLowHDI,
    onlyShowRecordsWithHiringData,
  ]);

  const sortedData = useMemo<AugmentedTimeZoneWithCountryData[]>(() => {
    if (sortField === 'overlap') {
      return [...filteredData].sort((a, b) =>
        sortDirection === 'desc'
          ? b.overlapInHours - a.overlapInHours
          : a.overlapInHours - b.overlapInHours
      );
    }

    if (sortField === 'countryName') {
      return [...filteredData].sort((a, b) =>
        sortDirection === 'desc'
          ? b.country.name.localeCompare(a.country.name)
          : a.country.name.localeCompare(b.country.name)
      );
    }

    if (sortField === 'hdi') {
      return [...filteredData].sort((a, b) => {
        if (!a.HDIData?.index) return 1;
        if (!b.HDIData?.index) return -1;

        return sortDirection === 'desc'
          ? b.HDIData?.index - a.HDIData?.index
          : a.HDIData?.index - b.HDIData?.index;
      });
    }

    return filteredData;
  }, [filteredData, sortField, sortDirection]);

  const searchCountriesOptions = useMemo(() => {
    const excludedCodes = ['RU', 'KP'];

    return uniqBy(augmentedWithExtraData, 'country.id')
      .filter((el) => !excludedCodes.includes(el.country.id))
      .filter((el) =>
        onlyShowRecordsWithLowHDI ? el.HDIData?.rating === 'LOW' : el
      )
      .filter((el) =>
        onlyShowRecordsWithHiringData ? !!el.hiringGuidesData?.url : el
      )
      .map((el) => el.country)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [
    augmentedWithExtraData,
    onlyShowRecordsWithLowHDI,
    onlyShowRecordsWithHiringData,
  ]);

  return {
    onlyShowRecordsWithLowHDI,
    searchValues,
    onlyShowRecordsWithHiringData,
    searchCountriesOptions,
    sortField,
    sortDirection,
    setOnlyShowRecordsWithLowHDI,
    setOnlyShowRecordsWithHiringData,
    setSorting,
    setSearchValues,
    sortedData,
  };
};
